import React, { Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';

// ─────────────────────────────────────────────────────────────────────────
// ROUTING — code-split via React.lazy
// Homepage and NotFound stay eager:
//   • Homepage is the most-common entry route; a Suspense flash on landing
//     would feel cheap on an editorial site.
//   • NotFound is tiny and avoids a fallback flash on a typo'd URL.
// Everything else is lazy. The Suspense fallback is editorial in spirit —
// a mono caps tag with a sienna pulse, matching the site's section markers.
// ─────────────────────────────────────────────────────────────────────────

import Homepage from './pages/Homepage.jsx';
import NotFound from './pages/NotFound.jsx';
import Footer from './components/Footer.jsx';

// Tools — all lazy. TechniqueLibrary in particular is enormous (~196K JSX);
// removing it from the main bundle is the single biggest win here.
const HardWaterScorer    = lazy(() => import('./tools/HardWaterScorer.jsx'));
const SolutionCalculator = lazy(() => import('./tools/SolutionCalculator.jsx'));
const TechniqueLibrary   = lazy(() => import('./tools/TechniqueLibrary.jsx'));
const CostEstimator      = lazy(() => import('./tools/CostEstimator.jsx'));
const TintIdentifier     = lazy(() => import('./tools/TintIdentifier.jsx'));
const StreakDiagnostic   = lazy(() => import('./tools/StreakDiagnostic.jsx'));
// s70 — 17 tools restored: components + guides + SEO entries existed, routes did not.
const BirdSoilingUrgency            = lazy(() => import('./tools/BirdSoilingUrgency.jsx'));
const CleaningScheduleBuilder       = lazy(() => import('./tools/CleaningScheduleBuilder.jsx'));
const ColdWeatherWindowCalculator   = lazy(() => import('./tools/ColdWeatherWindowCalculator.jsx'));
const DryingWindowPredictor         = lazy(() => import('./tools/DryingWindowPredictor.jsx'));
const FrameSubstrateIdentifier      = lazy(() => import('./tools/FrameSubstrateIdentifier.jsx'));
const HardWaterStainRemovalTriage   = lazy(() => import('./tools/HardWaterStainRemovalTriage.jsx'));
const HeritageGlassTriage           = lazy(() => import('./tools/HeritageGlassTriage.jsx'));
const IGUSelfCheck                  = lazy(() => import('./tools/IGUSelfCheck.jsx'));
const LadderSafetyCalculator        = lazy(() => import('./tools/LadderSafetyCalculator.jsx'));
const PoleReachCalculator           = lazy(() => import('./tools/PoleReachCalculator.jsx'));
const PureWaterTDSDiagnostic        = lazy(() => import('./tools/PureWaterTDSDiagnostic.jsx'));
const RegionalProtocolGenerator     = lazy(() => import('./tools/RegionalProtocolGenerator.jsx'));
const SaltSprayIntervalCalculator   = lazy(() => import('./tools/SaltSprayIntervalCalculator.jsx'));
const ScratchRiskCalculator         = lazy(() => import('./tools/ScratchRiskCalculator.jsx'));
const SolventLadderSelector         = lazy(() => import('./tools/SolventLadderSelector.jsx'));
const SqueegeeRubberTracker         = lazy(() => import('./tools/SqueegeeRubberTracker.jsx'));
const StorefrontFrequencyCalculator = lazy(() => import('./tools/StorefrontFrequencyCalculator.jsx'));

// Pages — all lazy. CityPage + StatePage carry the geo data weight,
// ArticlePage/BlogPost/GuidePage carry the react-markdown weight.
const ArticlePage         = lazy(() => import('./pages/ArticlePage.jsx'));
const ArticleIndex        = lazy(() => import('./pages/ArticleIndex.jsx'));
const GuidePage           = lazy(() => import('./pages/GuidePage.jsx'));
const StatePage           = lazy(() => import('./pages/StatePage.jsx'));
const CityPage            = lazy(() => import('./pages/CityPage.jsx'));
const StateIndex          = lazy(() => import('./pages/StateIndex.jsx'));
const Glossary            = lazy(() => import('./pages/Glossary.jsx'));
const BlogIndex           = lazy(() => import('./pages/BlogIndex.jsx'));
const BlogPost            = lazy(() => import('./pages/BlogPost.jsx'));
const NewsHub             = lazy(() => import('./pages/NewsHub.jsx'));
const ContributorIndex    = lazy(() => import('./pages/ContributorIndex.jsx'));
const ContributorPage     = lazy(() => import('./pages/ContributorPage.jsx'));
const EditorialStandards  = lazy(() => import('./pages/EditorialStandards.jsx'));
const Corrections         = lazy(() => import('./pages/Corrections.jsx')); // s139 Phase 6 corrections log
const Methodology         = lazy(() => import('./pages/Methodology.jsx'));
const Sources             = lazy(() => import('./pages/Sources.jsx'));
const Contact             = lazy(() => import('./pages/Contact.jsx'));
const Submit              = lazy(() => import('./pages/Submit.jsx'));
const FindACleaner        = lazy(() => import('./pages/FindACleaner.jsx'));
const WaterIndex          = lazy(() => import('./pages/WaterIndex.jsx'));
const WaterStatePage      = lazy(() => import('./pages/WaterStatePage.jsx'));
const WaterRankings       = lazy(() => import('./pages/WaterRankings.jsx'));

// ─────────────────────────────────────────────────────────────────────────
// Suspense fallback — editorial loading state
// Matches the site's typographic system: mono caps tag, sienna pulse dot,
// Fraunces serif accent. Held off-screen briefly via animation-delay so fast
// chunk loads feel instant; only chunks that take >120ms show the indicator.
// ─────────────────────────────────────────────────────────────────────────
function RouteLoading() {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Loading"
      style={{
        background: '#F4F1EA',
        color: '#1A1D1C',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Inter Tight', sans-serif",
      }}
    >
      <style>{`
        @keyframes wwgLoadingFade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes wwgLoadingPulse {
          0%, 100% { opacity: 0.3; transform: scale(0.85); }
          50%      { opacity: 1;   transform: scale(1); }
        }
        .wwg-loading {
          animation: wwgLoadingFade 0.4s ease-out 0.12s both;
        }
        .wwg-loading-dot {
          display: inline-block;
          width: 8px; height: 8px;
          background: #B5532A;
          border-radius: 50%;
          margin: 0 10px;
          animation: wwgLoadingPulse 1.1s ease-in-out infinite;
          vertical-align: middle;
        }
      `}</style>

      <div className="wwg-loading" style={{ textAlign: 'center' }}>
        <div style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 11,
          letterSpacing: '0.22em',
          color: '#5A5A52',
          textTransform: 'uppercase',
        }}>
          <span>One moment</span>
          <span className="wwg-loading-dot" aria-hidden="true" />
          <span>Setting the type</span>
        </div>
        <div style={{
          fontFamily: "'Fraunces', 'Times New Roman', serif",
          fontStyle: 'italic',
          fontSize: 13,
          color: '#A39E91',
          marginTop: 14,
        }}>
          Window Washing Guide
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Error boundary — surfaces silent React/chunk-load failures (s45).
//
// Without this, a lazy-import that fails (stale chunk URL after a deploy,
// network glitch fetching a code-split chunk, runtime error inside a
// page component) leaves the user staring at the Suspense fallback
// forever. We catch the error, log it for diagnostics, and render a
// minimal recovery UI that gives the user a "Reload" button and a
// "Back to home" link.
//
// Chunk-load failures get a slightly more useful message because they're
// the most common deployment-related failure mode and the fix
// (hard-refresh to get the new index.html with current chunk hashes) is
// different from a generic runtime crash.
// ─────────────────────────────────────────────────────────────────────────
function isChunkLoadError(err) {
  if (!err) return false;
  const msg = String(err && (err.message || err)) || '';
  return (
    err.name === 'ChunkLoadError' ||
    msg.indexOf('Failed to fetch dynamically imported module') !== -1 ||
    msg.indexOf('Loading chunk') !== -1 ||
    msg.indexOf("Importing a module script failed") !== -1
  );
}

class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    // Keep this around — the dev console will pick it up. In production
    // this is the only diagnostic surface for silent page-load failures.
    // eslint-disable-next-line no-console
    console.error('[RouteErrorBoundary]', error, info);
  }
  handleReload = () => {
    // Bypass cache on reload — for chunk-load errors this fetches a
    // fresh index.html with current chunk hashes.
    if (typeof window !== 'undefined') window.location.reload();
  };
  render() {
    if (!this.state.error) return this.props.children;
    const chunkFailure = isChunkLoadError(this.state.error);
    return (
      <div
        role="alert"
        style={{
          background: '#F4F1EA',
          color: '#1A1D1C',
          minHeight: '100vh',
          padding: '64px 24px',
          fontFamily: "'Inter Tight', sans-serif",
        }}
      >
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          <div style={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 11, letterSpacing: '0.08em',
            color: '#A39E91', textTransform: 'uppercase',
            marginBottom: 14,
          }}>
            {chunkFailure ? 'Page resources out of date' : 'Something went wrong'}
          </div>
          <h1 style={{
            fontFamily: "'Fraunces', 'Times New Roman', serif",
            fontWeight: 400, fontSize: 32, lineHeight: 1.15,
            margin: '0 0 14px',
          }}>
            {chunkFailure
              ? 'This page expects newer files than your browser has cached.'
              : 'This page hit an unexpected error.'}
          </h1>
          <p style={{
            fontFamily: "'Fraunces', 'Times New Roman', serif",
            fontStyle: 'italic', color: '#5A5A52', fontSize: 17,
            margin: '0 0 24px',
          }}>
            {chunkFailure
              ? 'Reloading should fix it. If the problem persists, the site may still be deploying — try again in a minute.'
              : 'The error has been logged. Reloading the page usually helps; if not, head back to the homepage.'}
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={this.handleReload}
              style={{
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                fontSize: 12, letterSpacing: '0.06em',
                textTransform: 'uppercase',
                background: '#B5532A', color: '#FAF7F1',
                border: 'none', padding: '10px 18px',
                cursor: 'pointer', borderRadius: 2,
              }}
            >
              Reload page
            </button>
            <a
              href="/"
              style={{
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                fontSize: 12, letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: '#1A1D1C', textDecoration: 'none',
                border: '1px solid #D9D3C5', padding: '10px 18px',
                borderRadius: 2,
              }}
            >
              Back to home
            </a>
          </div>
          {!chunkFailure && this.state.error && (
            <details open style={{
              marginTop: 32,
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 11, color: '#A39E91',
            }}>
              <summary style={{ cursor: 'pointer' }}>Error details</summary>
              <pre style={{
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                marginTop: 8, color: '#5A5A52',
                background: '#FAF7F1',
                border: '1px solid #D9D3C5',
                padding: 10,
                borderRadius: 2,
              }}>{(() => {
                // Safari's error.stack does NOT include the message line
                // that Chrome/Firefox put at the top, so we have to build
                // the full diagnostic string explicitly. Without this,
                // the error details surface as a stack trace with no
                // error message — useless for debugging.
                const err = this.state.error;
                const parts = [];
                const name = err && err.name ? err.name : 'Error';
                const message = err && err.message ? err.message : String(err);
                parts.push(name + ': ' + message);
                if (err && err.stack) {
                  // Skip duplicate "Name: message" line if browser already
                  // prepended it (Chrome/Firefox do, Safari doesn't).
                  const stackStr = String(err.stack);
                  const stackBody = stackStr.startsWith(name)
                    ? stackStr.slice(stackStr.indexOf('\n') + 1)
                    : stackStr;
                  parts.push(stackBody);
                }
                return parts.join('\n');
              })()}</pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}

export default function App() {
  return (
    <>
      <RouteErrorBoundary>
        <Suspense fallback={<RouteLoading />}>
          <Routes>
            <Route path="/" element={<Homepage />} />

            {/* Tools */}
            <Route path="/tools/streak-diagnostic" element={<StreakDiagnostic />} />
            <Route path="/tools/hard-water-scorer" element={<HardWaterScorer />} />
            <Route path="/tools/solution-calculator" element={<SolutionCalculator />} />
            <Route path="/tools/technique-library" element={<TechniqueLibrary />} />
            {/* s79: must be an explicit static route — see GuidePage.jsx header. */}
            <Route path="/tools/technique-library/guide" element={<GuidePage toolSlug="technique-library" />} />
            <Route path="/tools/technique-library/:chapterId" element={<TechniqueLibrary />} />
            <Route path="/tools/technique-library/:chapterId/:strokeId" element={<TechniqueLibrary />} />
            <Route path="/tools/cost-estimator" element={<CostEstimator />} />
            <Route path="/tools/tint-identifier" element={<TintIdentifier />} />

            {/* s70 — restored tool routes (previously soft-404s linked from their own guides) */}
            <Route path="/tools/bird-soiling-urgency" element={<BirdSoilingUrgency />} />
            <Route path="/tools/cleaning-schedule-builder" element={<CleaningScheduleBuilder />} />
            <Route path="/tools/cold-weather-window-calculator" element={<ColdWeatherWindowCalculator />} />
            <Route path="/tools/drying-window-predictor" element={<DryingWindowPredictor />} />
            <Route path="/tools/frame-substrate-identifier" element={<FrameSubstrateIdentifier />} />
            <Route path="/tools/hard-water-stain-removal" element={<HardWaterStainRemovalTriage />} />
            <Route path="/tools/heritage-glass-triage" element={<HeritageGlassTriage />} />
            <Route path="/tools/igu-self-check" element={<IGUSelfCheck />} />
            <Route path="/tools/ladder-safety-calculator" element={<LadderSafetyCalculator />} />
            <Route path="/tools/pole-reach-calculator" element={<PoleReachCalculator />} />
            <Route path="/tools/pure-water-tds-diagnostic" element={<PureWaterTDSDiagnostic />} />
            <Route path="/tools/regional-protocol-generator" element={<RegionalProtocolGenerator />} />
            <Route path="/tools/salt-spray-interval-calculator" element={<SaltSprayIntervalCalculator />} />
            <Route path="/tools/scratch-risk-calculator" element={<ScratchRiskCalculator />} />
            <Route path="/tools/solvent-ladder-selector" element={<SolventLadderSelector />} />
            <Route path="/tools/squeegee-rubber-tracker" element={<SqueegeeRubberTracker />} />
            <Route path="/tools/storefront-frequency" element={<StorefrontFrequencyCalculator />} />

            {/* Tool methodology guides — long-form deep dives */}
            <Route path="/tools/:toolSlug/guide" element={<GuidePage />} />

            {/* Articles */}
            <Route path="/articles" element={<ArticleIndex />} />
            <Route path="/articles/:slug" element={<ArticlePage />} />

            {/* Pillar landing pages — all use article index filtered */}
            <Route path="/encyclopedia" element={<ArticleIndex pillar="encyclopedia" />} />
            <Route path="/diagnostician" element={<ArticleIndex pillar="diagnostician" />} />
            <Route path="/field-notes" element={<ArticleIndex pillar="field-notes" />} />
            <Route path="/pro-track" element={<ArticleIndex pillar="pro-track" />} />

            {/* State landing pages */}
            <Route path="/states" element={<StateIndex />} />
            <Route path="/states/:slug" element={<StatePage />} />
            <Route path="/states/:stateSlug/:citySlug" element={<CityPage />} />

            {/* Water Atlas (s72, P3) — the citable data layer. /water is the
                national hardness table; /water/<state> is the state leaf.
                Distinct from /states/<state>, which answers "how do you clean
                here" rather than "how hard is the water here". Both carry
                self-referencing canonicals; see WaterStatePage.jsx header. */}
            <Route path="/water" element={<WaterIndex />} />
            {/* s134 Phase 5b — city rankings. A static segment, so React Router
                ranks it above /water/:slug; "rankings" is not a state slug. */}
            <Route path="/water/rankings" element={<WaterRankings />} />
            <Route path="/water/:slug" element={<WaterStatePage />} />

            {/* Glossary */}
            <Route path="/glossary" element={<Glossary />} />

            {/* Blog */}
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/:slug" element={<BlogPost />} />

            {/* The Wire — industry news hub (s69) */}
            <Route path="/news" element={<NewsHub />} />

            {/* Contributors */}
            <Route path="/contributors" element={<ContributorIndex />} />
            <Route path="/contributors/:slug" element={<ContributorPage />} />

            {/* About / Trust pages */}
            <Route path="/editorial-standards" element={<EditorialStandards />} />
            <Route path="/corrections" element={<Corrections />} />
            <Route path="/methodology" element={<Methodology />} />
            <Route path="/sources" element={<Sources />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/submit" element={<Submit />} />

            {/* Find-a-cleaner — placeholder destination for affiliate slots */}
            <Route path="/find-a-cleaner" element={<FindACleaner />} />

            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </RouteErrorBoundary>

      {/* s71 — site-wide footer.
          Sits outside the route table so every route gets one; it was
          inline in Homepage.jsx until now, so 1 of 440 pages had a footer.
          Outside Suspense so it paints immediately rather than behind a
          lazy route's fallback, and outside the error boundary so a
          crashed route still leaves the reader a way out. */}
      <Footer />
    </>
  );
}
